import React from 'react';
import { Root, Trigger, Portal, Content } from '@radix-ui/react-popover';
import pt from 'prop-types';

import SwellColorPickerPopup from './color-picker-popup';
import SwellGradientColorPicker from './color-picker-gradient';
import SwellColorPicker from './color-picker';

const PickerState = Object.freeze({
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
});

function SwellColorSchemeValue({
  path,
  type,
  label,
  value,
  presetColors,
  onChange,
}) {
  const isGradient = type === 'color_background';
  const [pickerState, setPickerState] = React.useState(PickerState.CLOSED);
  const initialColor = React.useRef(value);
  const [color, setColor] = React.useState(value);

  const onToggle = React.useCallback(() => {
    setPickerState((pickerState) => {
      if (pickerState === PickerState.CLOSING) {
        return pickerState;
      }

      if (pickerState === PickerState.CLOSED) {
        return PickerState.OPEN;
      }

      const { activeElement } = document;

      if (typeof activeElement?.blur === 'function') {
        // Prevent aria-hidden warning
        activeElement.blur();
      }

      return PickerState.CLOSING;
    });
  }, []);

  const onApply = React.useCallback(
    (color) => {
      setColor(color);

      onChange({ [path]: typeof color === 'string' ? color : color.hex });

      // setColor((color) => {
      //   onChange({ [path]: typeof color === 'string' ? color : color.hex });
      //   return color;
      // });

      // setPickerState(PickerState.CLOSING);

      // setTimeout(() => {
      //   setPickerState(PickerState.CLOSED);
      // }, 150);
    },
    [path, onChange, setColor],
  );

  const onCancel = React.useCallback(
    (event) => {
      if (event) {
        // Prevent aria-hidden warning
        event.target.blur();
      }

      setPickerState((pickerState) => {
        if (pickerState !== PickerState.OPEN) {
          return pickerState;
        }

        // setColor(value);
        onApply(initialColor.current);

        setTimeout(() => {
          setPickerState(PickerState.CLOSED);
        }, 150);

        return PickerState.CLOSING;
      });
    },
    [value, setColor],
  );

  const onClose = React.useCallback(() => {
    setPickerState((pickerState) => {
      if (pickerState !== PickerState.OPEN) {
        return pickerState;
      }

      setColor((color) => {
        initialColor.current = color;
        return color;
      });

      return PickerState.CLOSING;
    });
  }, []);

  const onPopupAnimationEnd = React.useCallback(() => {
    setPickerState((pickerState) => {
      if (pickerState !== PickerState.CLOSING) {
        return pickerState;
      }

      return PickerState.CLOSED;
    });
  }, []);

  /** @type {React.RefObject<HTMLElement | null>} */
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    /** @param {MouseEvent} event */
    function handleClickOutside(event) {
      if (!rootRef.current?.contains(event.target)) {
        // onCancel();

        onClose();
      }
    }

    if (pickerState !== PickerState.OPEN) {
      return () => {};
    }

    window.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, [pickerState, onCancel, onClose]);

  return (
    <article
      ref={rootRef}
      className="storefront-editor--sidebar-panel--body--article"
    >
      <Root open={pickerState !== PickerState.CLOSED} onOpenChange={onToggle}>
        <Trigger asChild>
          <button
            data-id={path}
            title={value || 'Not set'}
            aria-label={value || 'Not set'}
            className="storefront-editor--color-scheme"
            type="button"
          >
            <div
              className="storefront-editor--color-scheme--color"
              style={value ? { background: value } : undefined}
            >
              {value ? null : (
                <div className="storefront-editor--color-scheme--blank" />
              )}
            </div>

            <div className="storefront-editor--color-scheme--desc">
              <div className="storefront-editor--color-scheme--label">
                {label}
              </div>

              <div className="storefront-editor--color-scheme--value">
                {value || 'Not set'}
              </div>
            </div>
          </button>
        </Trigger>

        <Portal>
          <Content
            align="start"
            sideOffset={10}
            className="storefront-editor--color-scheme--picker-wrapper"
          >
            <SwellColorPickerPopup
              closing={pickerState === PickerState.CLOSING}
              onAnimationEnd={onPopupAnimationEnd}
            >
              {isGradient ? (
                <SwellGradientColorPicker
                  color={color}
                  presetColors={presetColors}
                  // onChange={setColor}
                  onChange={onApply}
                  onApply={onApply}
                  onCancel={onCancel}
                  onClose={onClose}
                />
              ) : (
                <SwellColorPicker
                  color={color}
                  presetColors={presetColors}
                  // onChange={setColor}
                  onChange={onApply}
                  onApply={onApply}
                  onCancel={onCancel}
                />
              )}
            </SwellColorPickerPopup>
          </Content>
        </Portal>
      </Root>
    </article>
  );
}

SwellColorSchemeValue.propTypes = {
  path: pt.string.isRequired,
  type: pt.string.isRequired,
  label: pt.string.isRequired,
  value: pt.oneOfType([pt.string, pt.shape({ hex: pt.string })]),
  presetColors: pt.arrayOf(pt.string).isRequired,
  onChange: pt.func.isRequired,
};

export default React.memo(SwellColorSchemeValue);
